import {Injectable,  Inject, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';

declare var gtag: Function;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  constructor(private router: Router, @Inject(PLATFORM_ID) private readonly platformId: any) {
  }

  public event(eventName: string, params: {}) {
    gtag('event', eventName, params);
  }

  public init(googleAnalyticsKey: any) {
    if (this.platformId === 'browser') {
      this.listenForRouteChanges(googleAnalyticsKey);

      try {
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = 'https://www.googletagmanager.com/gtag/js?id=' + googleAnalyticsKey;
        document.head.appendChild(script1);

        const script2 = document.createElement('script');
        script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '` + googleAnalyticsKey + `', {'send_page_view': true});
        `;
        document.head.appendChild(script2);
      } catch (ex) {
        console.error('Error appending google analytics');
        console.error(ex);
      }
    }
  }

  private listenForRouteChanges(googleAnalyticsKey: any) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        gtag('config', googleAnalyticsKey, {
          'page_path': event.urlAfterRedirects,
        });
      }
    });
  }

  public createSchema(schemaCodes: any) {
    if (this.platformId === 'browser') {
      const script3 = document.createElement('script');
      script3.type = 'application/ld+json';
      script3.innerHTML = schemaCodes;
      document.head.appendChild(script3);
    }
  }

  public createGooglePurchaseTracking(code: string) {
    if(!code) return;
    if (this.platformId === 'browser') {
      const script = document.createElement('script');
      script.setAttribute('id', 'googlePurchaseCode');
      script.type  = "text/javascript";
      script.text = code;
      document.body.appendChild(script);
    }
  }

  createCustomJavaScript(code: string) {
    //create it into the head tag
    if (!code) return;
    if (this.platformId === 'browser') {
      const scriptSeparator = '/*__CUSTOM_SCRIPT_SPLIT__*/';
      let normalizedCode = String(code);
      const externalScripts: Array<{ src: string; async: boolean }> = [];

      const inlineScriptPattern = /\/\*__CUSTOM_INLINE_SCRIPT_SPLIT__\*\/([\s\S]*?)(?=\/\*__CUSTOM_SCRIPT_SPLIT__\*\/|$)/g;
      normalizedCode = normalizedCode.replace(inlineScriptPattern, (_, inlineBlock: string) => {
        const srcPattern = /(async\s+)?src\s*=\s*["']([^"']+)["']/g;
        let match: RegExpExecArray | null;

        while ((match = srcPattern.exec(inlineBlock)) !== null) {
          externalScripts.push({
            async: Boolean(match[1]),
            src: match[2]
          });
        }

        return '';
      });

      const scriptBlocks = normalizedCode
        .split(scriptSeparator)
        .map(block => block.trim())
        .filter(Boolean);

      if (!externalScripts.length && !scriptBlocks.length) return;

      document.head
        .querySelectorAll('script[id^="customCode"], script[id^="customInlineScript"]')
        .forEach(existing => existing.remove());

      externalScripts.forEach((externalScript, index) => {
        const script = document.createElement('script');
        script.setAttribute('id', `customInlineScript${index + 1}`);
        script.src = externalScript.src;
        if (externalScript.async) {
          script.async = true;
        }
        document.head.appendChild(script);
      });

      scriptBlocks.forEach((block, index) => {
        const script = document.createElement('script');
        script.setAttribute('id', scriptBlocks.length === 1 ? 'customCode' : `customCode${index + 1}`);
        script.type = 'text/javascript';
        script.text = block;
        document.head.appendChild(script);
      });
    } else {
      console.warn('Custom JavaScript code can only be executed in the browser environment.');
    }
  }
  //create custom <noscript> tag for google tag manager / meta pixel
  createCustomNoScript(code: string) {
    if (!code) return;
    if (this.platformId !== 'browser') return;

    const parser = new DOMParser();
    const parsed = parser.parseFromString(code, 'text/html');
    const noScriptElements = Array.from(parsed.querySelectorAll('iframe, img'));
    if (!noScriptElements.length) return;

    const fragment = document.createDocumentFragment();

    noScriptElements.forEach(element => {
      if (element instanceof HTMLIFrameElement) {
        const iframeSrc = element.getAttribute('src') || '';
        if (!iframeSrc) return;

        const existingIframe = Array.from(document.body.querySelectorAll('iframe'))
          .some(existing => existing.getAttribute('src') === iframeSrc);

        if (existingIframe) {
          return;
        }

        const newIframe = document.createElement('iframe');
        newIframe.src = iframeSrc;
        newIframe.height = element.getAttribute('height') || '0';
        newIframe.width = element.getAttribute('width') || '0';
        newIframe.setAttribute('title', 'custom-noscript-iframe');
        newIframe.setAttribute('style', element.getAttribute('style') || 'display:none;visibility:hidden');

        fragment.appendChild(newIframe);
        return;
      }

      if (element instanceof HTMLImageElement) {
        const imgSrc = element.getAttribute('src') || '';
        if (!imgSrc) return;

        const existingImg = Array.from(document.body.querySelectorAll('img'))
          .some(existing => existing.getAttribute('src') === imgSrc);

        if (existingImg) {
          return;
        }

        const newImg = document.createElement('img');
        newImg.src = imgSrc;
        newImg.setAttribute('height', element.getAttribute('height') || '1');
        newImg.setAttribute('width', element.getAttribute('width') || '1');
        newImg.setAttribute('alt', element.getAttribute('alt') || 'custom-noscript-image');
        newImg.setAttribute('style', element.getAttribute('style') || 'display:none');

        fragment.appendChild(newImg);
      }
    });

    if (!fragment.childNodes.length) return;

    const firstChild = document.body.firstChild;
    if (firstChild) {
      document.body.insertBefore(fragment, firstChild);
    } else {
      document.body.appendChild(fragment);
    }
  }


}
